import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Session } from "@/types";

// Sub-Resource-Hooks für Sessions pro Agent.
// QueryKey: ["resources","agents",agentId,"sessions","list"]
//           ["resources","agents",agentId,"sessions",sessionId]
//
// Pattern wie M1: apiClient on-call (Auth-Hydration), enabled bei !!session.

export function useAgentSessions(agentId: string | undefined): UseQueryResult<Session[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: agentId
      ? queryKeys.subList("agents", agentId, "sessions")
      : ["resources", "agents", "_invalid", "sessions", "list"],
    queryFn: async () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      const res = await client.get<{ sessions: Session[] }>(`/agents/${agentId}/sessions`);
      return res.sessions;
    },
    enabled: !!agentId && !!session,
  });
}

export function useAgentSession(
  agentId: string | undefined,
  sessionId: string | undefined,
): UseQueryResult<Session> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey:
      agentId && sessionId
        ? queryKeys.subDetail("agents", agentId, "sessions", sessionId)
        : ["resources", "agents", "_invalid", "sessions", "_invalid"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<Session>(`/agents/${agentId}/sessions/${sessionId}`);
    },
    enabled: !!agentId && !!sessionId && !!session,
  });
}
